/**
 * Message-history drawer (2026-08 contact finish line): the company-scoped
 * list of every contact send, newest first, grouped one entry per send (batch)
 * with per-recipient engagement statuses maintained by the SendGrid Event
 * Webhook. Non-live sends are badged as test deliveries — the sender should
 * see their own rehearsals, clearly marked.
 *
 * Hosted in the header (both hosts mount maps-header, so no host-app edits);
 * open/close state lives in ContactService so the kebab menu's Messages item
 * can toggle it.
 * Structure mirrors the saved-searches drawer.
 */
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ContactService } from '../../services/contact.service';
import { NotifyError } from '../../services/notify';
import { GroupContactHistory, type ContactHistoryBatchView } from '../../utils/contact-status';
import type { MapsContactHistoryRow } from '../../models/maps.types';

const PAGE = 50;

@Component({
    selector: 'maps-message-history-drawer',
    standalone: false,
    templateUrl: './message-history-drawer.component.html',
    styleUrl: './message-history-drawer.component.scss',
})
export class MessageHistoryDrawerComponent implements OnInit, OnDestroy {
    open = false;
    batches: ContactHistoryBatchView[] = [];
    totalCount = 0;
    isLoading = false;
    isLoadingMore = false;

    private rows: MapsContactHistoryRow[] = [];
    private subscription: Subscription | null = null;

    constructor(
        private readonly contact: ContactService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.subscription = this.contact.HistoryDrawerOpen$.subscribe((open) => {
            this.zone.run(() => {
                this.open = open;
                this.cdr.detectChanges();
            });
            if (open) void this.LoadFirstPage();
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    /** hasMore compares ROW counts — the server pages rows, not batches. */
    get hasMore(): boolean {
        return this.rows.length < this.totalCount;
    }

    OnClosed(): void {
        this.contact.CloseHistoryDrawer();
    }

    /** Fresh first page on every open — webhook events move statuses between opens. */
    private async LoadFirstPage(): Promise<void> {
        this.isLoading = true;
        try {
            const page = await this.contact.History(PAGE, 0);
            this.zone.run(() => {
                this.rows = page.items;
                this.batches = GroupContactHistory(this.rows);
                this.totalCount = page.totalCount;
                this.isLoading = false;
                this.cdr.detectChanges();
            });
        } catch {
            this.zone.run(() => {
                this.isLoading = false;
                NotifyError('Could not load messages');
                this.cdr.detectChanges();
            });
        }
    }

    async LoadMore(): Promise<void> {
        if (this.isLoadingMore || !this.hasMore) return;
        this.isLoadingMore = true;
        try {
            const page = await this.contact.History(PAGE, this.rows.length);
            this.zone.run(() => {
                this.rows = [...this.rows, ...page.items];
                this.batches = GroupContactHistory(this.rows);
                this.totalCount = page.totalCount;
                this.cdr.detectChanges();
            });
        } catch {
            NotifyError('Could not load more messages');
        } finally {
            this.zone.run(() => {
                this.isLoadingMore = false;
                this.cdr.detectChanges();
            });
        }
    }

    TrackByBatch(_ix: number, batch: ContactHistoryBatchView): string {
        return batch.batchId;
    }
}
